/**
 * Custom `next/image` loader that routes image URLs through Cloudflare
 * Image Resizing (`/cdn-cgi/image/...`) in production. The previous
 * config used `images.unoptimized: true` on Cloudflare Workers because
 * `sharp` doesn't run in the Workers runtime — but that meant raw PNGs
 * (some hero images are 2-4 MB) were shipped at full size to every
 * visitor. This loader generates URLs that Cloudflare's edge resizes
 * on the fly and returns as AVIF/WebP via `format=auto`, which is the
 * single biggest CWV win for this site.
 *
 * Requires Transformations (formerly "Image Resizing") to be enabled on the
 * Cloudflare zone: dashboard → Images → Transformations (older UI: Speed →
 * Optimization → Image Resizing). Billed per unique transformation on Workers
 * Paid (cached after first hit). If it is disabled the `/cdn-cgi/image/...`
 * URL will 404 — set `NEXT_PUBLIC_USE_CLOUDFLARE_IMAGE_RESIZING=false` as a
 * build variable and rebuild to use raw sources consistently on both the
 * server and client.
 *
 * PERF-1 (2026-07-06): this loader also gates on the client-visible
 * `NEXT_PUBLIC_USE_CLOUDFLARE_IMAGE_RESIZING` flag. next/image runs the loader
 * on BOTH server (SSR) and client (hydration); reading the same `NEXT_PUBLIC_`
 * value (inlined identically into both bundles at build) guarantees the two
 * produce the same URL, so there's no hydration mismatch when the flag flips.
 * next.config wires this loader unless that flag is explicitly "false", so this
 * remains belt-and-suspenders while keeping the loader self-consistent.
 *
 * Dev: Cloudflare's `/cdn-cgi/...` path doesn't exist on `next dev`, so
 * we always pass the raw src through locally.
 */

interface LoaderArgs {
  src: string;
  width: number;
  quality?: number;
}

export default function cloudflareImageLoader({
  src,
  width,
  quality,
}: LoaderArgs): string {
  // Local dev or explicit public opt-out — return the raw src untouched.
  // `next dev` has no Cloudflare endpoint; the public build flag is the only
  // rollback path because it is identical server- and client-side (PERF-1),
  // so hydration cannot diverge.
  if (
    process.env.NODE_ENV !== "production" ||
    process.env.NEXT_PUBLIC_USE_CLOUDFLARE_IMAGE_RESIZING === "false"
  ) {
    return src;
  }
  // Cloudflare Image Resizing returns 415 for local AVIF source files. These
  // bundled assets are already compact AVIFs, so serving the original is both
  // safe and smaller than a failed transform followed by a retry.
  if (/^\/asset\/.+\.avif(?:\?.*)?$/i.test(src)) return src;
  // Already routed (e.g. someone hand-built a /cdn-cgi/ URL) — pass through.
  if (src.startsWith("/cdn-cgi/")) return src;

  const params = [
    `width=${width}`,
    `quality=${quality ?? 75}`,
    "format=auto",
  ].join(",");

  // Cross-origin (R2 public URLs etc.) — CF Image Resizing accepts an
  // absolute source URL after the params segment.
  if (/^https?:\/\//i.test(src)) {
    return `/cdn-cgi/image/${params}/${src}`;
  }

  // Local path — `/asset/...` or similar. Ensure leading slash exactly once.
  const path = src.startsWith("/") ? src : `/${src}`;
  return `/cdn-cgi/image/${params}${path}`;
}
